import { Injectable, inject } from "@angular/core";
import { BudgetTimeframesType } from '@shared/types/budget.interface';
import { TabSwitchOption } from '@shared/components/tab-switch/tab-switch.types';
import { BehaviorSubject, filter, take } from "rxjs";
import { TimeframeConfigDialogService } from "../../../dialogs/timeframe-config-dialog/timeframe-config-dialog.service";
import { TableConfigApiPayload, TableConfigDialogService } from '../../../dialogs/table-config-dialog/table-config-dialog.service';
import { managePageUserPreferenceStrings } from "../../../constants/user-preferences.constants";
import { Budget } from "../../../../shared/types/budget.interface";
import { UtilityService } from "../../../../shared/services/utility.service";
import ManageCegTableContainerStore from '../manage-ceg-table-container.store';
import { CustomAvailableBudgetConfigurationService } from "app/custom-available-budget/custom-available-budget-configuration.service";
import { newCustomAvailableBudgetManageTableConfiguration } from "@manage-ceg/constants/manage-ceg-page.constants";

// created class using the existing standard based on
// manage-ceg-table-mode.service.ts
@Injectable()
export class MctcTableModeService {
  private readonly utilityService = inject(UtilityService);
  private readonly tableConfigService = inject(TableConfigDialogService);
  private readonly timeframeConfigService = inject(TimeframeConfigDialogService);
  private readonly customAvailableBudgetConfigurationService = inject(CustomAvailableBudgetConfigurationService);

  private _presentationTimeframeOptions: Record<BudgetTimeframesType, TabSwitchOption> = {
    [BudgetTimeframesType.Month]: {
      title: 'M',
      value: BudgetTimeframesType.Month,
      hidden: false,
      tooltip: 'Month'
    },
    [BudgetTimeframesType.Quarter]: {
      title: 'Q',
      value: BudgetTimeframesType.Quarter,
      hidden: false,
      tooltip: 'Quarter'
    },
    [BudgetTimeframesType.Year]: {
      title: 'Y',
      value: BudgetTimeframesType.Year,
      hidden: false,
      tooltip: 'Year'
    },
  };
  public presentationTimeframeOptions: TabSwitchOption[] = this.valuesToFillPresentationTimeframeOptions();
  private readonly _presentationTimeframeMode$ = new BehaviorSubject<BudgetTimeframesType>(undefined);
  private readonly _presentationTimeframeIds$ = new BehaviorSubject<(string | number)[]>(undefined);

  public presentationTimeframeMode$ = this._presentationTimeframeMode$.asObservable().pipe(
    filter(mode => !!mode)
  );
  public presentationTimeframeIds$ = this._presentationTimeframeIds$.asObservable().pipe(
    filter(value => !!value)
  );

  get presentationTimeframeMode(): BudgetTimeframesType {
    return this._presentationTimeframeMode$.getValue();
  }

  get presentationTimeframeIds(): (string | number)[] {
    return this._presentationTimeframeIds$.getValue();
  }

  public updatePresentationTimeframeOptions(
    budgetMode: BudgetTimeframesType
  ): void {
    this._presentationTimeframeOptions[
      BudgetTimeframesType.Month
    ].hidden = budgetMode !== BudgetTimeframesType.Month;

    this._presentationTimeframeOptions[
      BudgetTimeframesType.Quarter
    ].hidden = budgetMode === BudgetTimeframesType.Year;

    this.presentationTimeframeOptions = this.valuesToFillPresentationTimeframeOptions();
  }

  private valuesToFillPresentationTimeframeOptions(): TabSwitchOption[] {
    return Object.values(
      this._presentationTimeframeOptions
    ).filter(option => !option.hidden);
  }

  public toggleAllPresentationTimeframeOptions(disabled: boolean): void {
    const presentationTimeframesState =  { ...this._presentationTimeframeOptions}
    Object.values(presentationTimeframesState).forEach(option => {
      option.disabled = disabled;
    });
    this._presentationTimeframeOptions = presentationTimeframesState;
    this.presentationTimeframeOptions = this.valuesToFillPresentationTimeframeOptions();
  }

  public setPresentationTimeframeMode(
    companyId: number,
    budgetId: number,
    timeframeMode: BudgetTimeframesType,
    timeframeIds: (string | number)[] = []
  ): void {
    this.saveTimeframeMode(
      companyId, budgetId, timeframeMode
    ).then(() => {
      this.emitPresentationTimeframeMode(timeframeMode, timeframeIds);
    }).catch(err => {
      this.utilityService.showToast(
        { Title: '', Message: 'Please retry' }
      );
      console.error(err);
    });
  }

  public emitPresentationTimeframeMode(
    timeframeMode: BudgetTimeframesType, timeframeIds: (string | number)[]
  ): void {
    this._presentationTimeframeMode$.next(timeframeMode);
    this.setPresentationTimeframeIds(timeframeIds);
  }

  public setPresentationTimeframeIds(ids: (string | number)[]): void {
    this._presentationTimeframeIds$.next(ids);
  }

  public saveTimeframeMode(
    companyId: number, budgetId: number, timeframeMode
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.timeframeConfigService.postUserTimeframeConfig(
        companyId, budgetId, {mode: timeframeMode,timeframeIds: []}
      ).pipe(take(1)).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (error) => {
          reject(error);
          console.error(error);
        }
      })
    });
  }

  public handleApiResponse(budget: Budget, response): void {
    const budgetTableAreaDetails = managePageUserPreferenceStrings['budget_table'].personalization_keys;
    let tableConfigData = response[budgetTableAreaDetails.table_config];
    let availableColumnId = ManageCegTableContainerStore.dbId2TableColumnNameMap['available'];
    if(tableConfigData?.columns_to_show?.length && !tableConfigData.columns_to_show.includes(availableColumnId)) {
      tableConfigData.columns_to_show.push(availableColumnId); // Making sure that available column is always present
    }

    let timeframeConfigData = response[budgetTableAreaDetails.timeframe_config];
    timeframeConfigData = this.transformTimeframeConfigData(timeframeConfigData, budget);
    const transformedTableConfigData = this.transformTableConfigData(tableConfigData);
    this.emitPresentationTimeframeMode(timeframeConfigData.mode, timeframeConfigData.ids);

    this.timeframeConfigService.userTimeframeConfig = timeframeConfigData;
    this.tableConfigService.userTableViewConfig = transformedTableConfigData;

    
    
    if(tableConfigData) {
      this.customAvailableBudgetConfigurationService.checkBudgetFormulaWithUserPreferenceCABFormula(tableConfigData.columns_to_show);
      const formulaItems = this.getFormulaItemsFromSavedUserPreference(tableConfigData.columns_to_show);
      this.customAvailableBudgetConfigurationService.setManagePageCustomAvailableBudgetFormulaItems(formulaItems);   
    } else {
      this.customAvailableBudgetConfigurationService.setManagePageCustomAvailableBudgetFormulaOverride(false);
    }
  }

  private getFormulaItemsFromSavedUserPreference(columnsToShow: number[]): string[] {
    const formulaItems: string[] = []
    columnsToShow.forEach((colId: number) => {
        const property = this.getColumnPropertyFromId(colId);
        if (property) {
          const formulaLabel = newCustomAvailableBudgetManageTableConfiguration.columns.find(item => item.key === property).label
          formulaItems.push(formulaLabel);
        }
    })
    return formulaItems; 
 
  }

  private getColumnPropertyFromId(id): string {
    const map = ManageCegTableContainerStore.dbId2TableColumnNameMap;
    for (const key in map) {
      if (map[key] == id) {
        return key;
      }
    }
    throw new Error(`Something is corrupted here`);
  }

  private transformTimeframeConfigData(config, budget: Budget): any {
    if (!config) {
      // NOTE FOR CAB: Default set here for timeframeConfig Mode
      return {
        budgetId: budget.id,
        mode: budget.type,
        ids: []
      };
    }
    const transformedData = {
      budgetId: budget.id,
      mode: config.mode,
      ids: config.ids
    }
    if (config.mode === budget.type) {
      transformedData['ids'] = this.timeframeConfigService.convertRelativeIdsToAllocIds(config.ids)
    }
    return transformedData;
  }

  private transformTableConfigData(shortenedConfigdata: TableConfigApiPayload): any {
    if (!shortenedConfigdata) {
      return shortenedConfigdata;
    }
    const mapKeys = Object.keys(ManageCegTableContainerStore.dbId2TableColumnNameMap);
    const payload = {
      columns: {...shortenedConfigdata.misc.columns_to_show },
      columnsFormula: shortenedConfigdata.misc.columnsFormula,
      segmentBreakdownData: shortenedConfigdata.misc.segmentBreakdownData
    };
    shortenedConfigdata.columns_to_show.forEach((id) => {
      const property = this.getColumnPropertyFromId(id);
      if (property) {
        payload.columns[property] = true;
      }
    });
    const payloadColumnKeys = Object.keys(payload.columns);
    const leftOverColumns = Object.keys(
      mapKeys
    ).filter(key => payloadColumnKeys.includes(key));
    leftOverColumns.forEach((key) => {
      payload.columns[key] = false;
    });
    payload.columns['available'] = true; // always show available column
    return payload;
  }
}
